import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@/components/ui/AppIcon';
import { mediaUrl } from '@/lib/api';
import { isEventBookingOpen } from '@/lib/eventAdapter';

export default function EventsCarouselSection({
    id,
    ariaLabel,
    sectionLabel,
    title,
    events,
    loadError,
    emptyMessage,
    showViewAll = true,
    className = '',
}) {
    const sectionRef = useRef(null);
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.querySelectorAll('.reveal-up').forEach((el) => {
                        el.classList.add('revealed');
                    });
                    entry.target.querySelectorAll('.reveal-fade').forEach((el) => {
                        el.classList.add('revealed');
                    });
                }
            });
        }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!events.length) return undefined;

        const frame = requestAnimationFrame(() => {
            sectionRef.current?.querySelectorAll('.reveal-up, .reveal-fade').forEach((el) => {
                el.classList.add('revealed');
            });
            updateScrollState();
        });

        return () => cancelAnimationFrame(frame);
    }, [events]);

    const updateScrollState = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollState();
        el.addEventListener('scroll', updateScrollState);
        window.addEventListener('resize', updateScrollState);
        return () => {
            el.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
        };
    }, [events]);

    const scrollLeft = () => {
        scrollRef.current?.scrollBy({ left: -320, behavior: 'smooth' });
    };

    const scrollRight = () => {
        scrollRef.current?.scrollBy({ left: 320, behavior: 'smooth' });
    };

    return (
        <section ref={sectionRef} id={id} className={`bg-background py-16 lg:py-20 ${className}`} aria-label={ariaLabel}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-10">
                    <div className="reveal-up">
                        <p className="section-label mb-2">{sectionLabel}</p>
                        <h2 className="font-display text-section-title font-semibold text-foreground gold-underline">
                            {title}
                        </h2>
                    </div>
                    {showViewAll ? (
                        <Link
                            to="/events"
                            className="reveal-fade btn-outline-gold flex items-center gap-2 px-5 py-2.5 rounded-full text-xs self-start sm:self-auto"
                        >
                            <span>View All Events</span>
                            <Icon name="ArrowRightIcon" size={13} />
                        </Link>
                    ) : null}
                </div>

                <div className="relative flex items-center gap-3">
                    <button
                        type="button"
                        onClick={scrollLeft}
                        aria-label="Scroll left"
                        className={`flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-200 z-10 ${canScrollLeft
                            ? 'border-[#ebb413] text-[#ebb413] hover:bg-[#ebb413] hover:text-black cursor-pointer'
                            : 'border-foreground/20 text-foreground/20 cursor-not-allowed'}`}
                    >
                        <Icon name="ChevronLeftIcon" size={20} />
                    </button>

                    <div
                        ref={scrollRef}
                        className="flex gap-5 overflow-x-auto scrollbar-hide flex-1 md:[scroll-snap-type:none] [scroll-snap-type:x_mandatory]"
                        style={{ scrollPaddingLeft: '0px' }}
                    >
                        {loadError ? (
                            <p className="text-muted-foreground py-8">{loadError}</p>
                        ) : null}
                        {!loadError && events.length === 0 ? (
                            <p className="text-muted-foreground py-8">{emptyMessage}</p>
                        ) : null}
                        {events.map((event, i) => (
                            <div
                                key={event.id}
                                className={`reveal-up revealed stagger-${i + 1} group flex flex-col flex-shrink-0 md:[scroll-snap-align:none] [scroll-snap-align:center] event-card-desktop`}
                                style={{ width: 'calc(25% - 15px)', minWidth: 'clamp(200px, 65vw, 260px)' }}
                            >
                                <div className="w-full flex-shrink-0 event-image-wrapper">
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={mediaUrl(event.image)}
                                        alt={event.alt}
                                        className="w-full block transition-transform duration-700 event-image"
                                        loading="lazy"
                                    />
                                </div>

                                <div className="flex justify-center" style={{ marginTop: '-22px', position: 'relative', zIndex: 10 }}>
                                    {isEventBookingOpen(event) ? (
                                        <Link
                                            to={`/events/${event.slug}`}
                                            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase text-black shadow-md transition-transform duration-200 hover:scale-110"
                                            style={{ backgroundColor: '#ebb413' }}
                                        >
                                            BOOK MY SEAT
                                        </Link>
                                    ) : (
                                        <span
                                            aria-disabled="true"
                                            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-xs font-bold tracking-widest uppercase text-black/60 shadow-md cursor-not-allowed opacity-60"
                                            style={{ backgroundColor: '#ebb413' }}
                                        >
                                            BOOK MY SEAT
                                        </span>
                                    )}
                                </div>

                                <div className="mt-2 flex flex-col items-center gap-0.5 text-center">
                                    <span className="text-sm font-medium text-foreground">{event.date}</span>
                                    <span className="text-xs text-foreground/60">{event.city}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={scrollRight}
                        aria-label="Scroll right"
                        className={`flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-200 z-10 ${canScrollRight
                            ? 'border-[#ebb413] text-[#ebb413] hover:bg-[#ebb413] hover:text-black cursor-pointer'
                            : 'border-foreground/20 text-foreground/20 cursor-not-allowed'}`}
                    >
                        <Icon name="ChevronRightIcon" size={20} />
                    </button>
                </div>
            </div>
        </section>
    );
}
